import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Badge } from '../../components/ui';
import { RATING_BADGE } from '../../lib/statusMaps';
import { humanize } from '../../lib/format';

/**
 * End-client name field backed by the end-client register.
 * Select existing, or add a new one to the list when missing.
 */
export default function EndClientPicker({
  value = '',
  onChange,
  onSelect,
  showToast,
  disabled = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [rating, setRating] = useState('not_classified');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const load = () => {
    setLoading(true);
    dealMgmt.endClients()
      .then((res) => setItems(res.items || res.end_clients || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 12);
    return items
      .filter((ec) => String(ec.name || '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [items, query]);

  const exact = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return items.find((ec) => String(ec.name || '').toLowerCase() === q) || null;
  }, [items, query]);

  const selected = useMemo(() => {
    const q = String(value || '').trim().toLowerCase();
    if (!q) return null;
    return items.find((ec) => String(ec.name || '').toLowerCase() === q) || null;
  }, [items, value]);

  const pick = (ec) => {
    setQuery(ec.name);
    onChange?.(ec.name);
    onSelect?.(ec);
    setOpen(false);
  };

  const addNew = async () => {
    const name = query.trim();
    if (!name || busy) return;
    if (exact) {
      pick(exact);
      return;
    }
    setBusy(true);
    try {
      await dealMgmt.createEndClient({ name, rating });
      showToast?.(`End-client “${name}” added to register`);
      const res = await dealMgmt.endClients();
      const next = res.items || res.end_clients || [];
      setItems(next);
      const created = next.find((ec) => String(ec.name).toLowerCase() === name.toLowerCase())
        || { name, rating };
      pick(created);
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <label className="field-label">End-client name</label>
      <input
        className="field-input"
        value={query}
        disabled={disabled}
        placeholder={loading ? 'Loading end-clients…' : 'Search or add end-client…'}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange?.(e.target.value);
          setOpen(true);
        }}
        autoComplete="off"
      />

      {selected && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge type={RATING_BADGE[selected.rating] || 'neutral'}>
            {humanize(selected.rating)}
          </Badge>
          <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>From end-client register</span>
        </div>
      )}

      {open && !disabled && (
        <div
          style={{
            position: 'absolute',
            zIndex: 40,
            left: 0,
            right: 0,
            top: 'calc(100% + 4px)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-md)',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--text3)' }}>Loading…</div>
          )}
          {!loading && filtered.map((ec) => (
            <button
              key={ec.id || ec.name}
              type="button"
              onClick={() => pick(ec)}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '9px 12px',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>{ec.name}</span>
              <Badge type={RATING_BADGE[ec.rating] || 'neutral'}>{humanize(ec.rating)}</Badge>
            </button>
          ))}

          {!loading && filtered.length === 0 && query.trim() && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>
              No match in end-client list
            </div>
          )}

          {!loading && query.trim() && !exact && (
            <div style={{ padding: 12, borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
              <div style={{ fontSize: 12, fontWeight: 650, marginBottom: 8 }}>
                Add “{query.trim()}” to end-client list
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  className="field-input"
                  style={{ flex: 1 }}
                  value={rating}
                  onChange={(e) => setRating(e.target.value)}
                >
                  <option value="green">Green</option>
                  <option value="amber">Amber</option>
                  <option value="red">Red</option>
                  <option value="not_classified">Not classified</option>
                </select>
                <button type="button" className="btn btn-p btn-sm" disabled={busy} onClick={addNew}>
                  {busy ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
